import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RecReviewStatus } from 'graphql/gql-types';

import { dataDate } from 'features/dates/date-helpers';
import type { GetPropertyRecRatesQuery } from 'features/rec-rates/gql/_gen_/property-rec-rates.gql';

type RecRates = GetPropertyRecRatesQuery['getPropertyRecRates'];

/**
 * A rec rate that may also carry the live BWH rate for its stay date.
 *
 * `getPropertyRecRates` and the accept/reset mutations do not select any live
 * fields — live data lives on the separate `LiveData` type and is merged into
 * grid rows by stay date. Rows dispatched from the grid therefore carry
 * `current_rate_live`, while rows coming straight from those queries do not.
 */
export type PendingRec = NonNullable<RecRates>[number] & {
	current_rate_live?: number | null;
};

export interface RateUploadState {
	allRowsSelected?: boolean;
	jobId?: string;
	pendingRecs: PendingRec[];
	polling: boolean;
	recRates: PendingRec[];
	uploading: boolean;
}

const initialState: RateUploadState = {
	allRowsSelected: false,
	jobId: undefined,
	pendingRecs: [],
	polling: false,
	recRates: [],
	uploading: false,
};

export const filterRecs = (recs?: PendingRec[], status = RecReviewStatus.P) => {
	return recs?.filter((rec) => rec?.rec_status === status) ?? [];
};

/**
 * Carry `current_rate_live` from `known` onto any incoming rec that is missing
 * it, matching on stay date.
 *
 * Incoming recs come from queries/mutations that cannot select live fields, so
 * replacing state wholesale would drop the live BWH rate and make the upload
 * confirmation dialog show a stale rate. Only the live field is carried over —
 * every other value from the incoming rec wins, since it is the fresher record.
 */
export const withLiveRates = (
	incoming?: PendingRec[],
	known?: PendingRec[],
): PendingRec[] => {
	if (!incoming?.length) return [];
	if (!known?.length) return [...incoming];

	const liveRateByDate = new Map<string, number | null | undefined>();
	for (const rec of known) {
		if (rec?.current_rate_live == null) continue;
		liveRateByDate.set(dataDate(rec.stay_date), rec.current_rate_live);
	}
	if (!liveRateByDate.size) return [...incoming];

	return incoming.map((rec) => {
		if (rec?.current_rate_live != null) return rec;
		const liveRate = liveRateByDate.get(dataDate(rec?.stay_date));
		return liveRate == null ? rec : { ...rec, current_rate_live: liveRate };
	});
};

export const removeRec = (recs?: PendingRec[], recDate?: string) => {
	return (
		recs?.filter(
			(rec) => rec?.stay_date && dataDate(rec.stay_date) !== dataDate(recDate),
		) ?? []
	);
};

export const rateUploadSlice = createSlice({
	name: 'rateUpload',
	initialState,
	reducers: {
		setJobId(state, action: PayloadAction<string | undefined>) {
			state.jobId = action.payload;
		},
		setPendingRecs(state, action: PayloadAction<PendingRec[] | undefined>) {
			state.pendingRecs = filterRecs(
				withLiveRates(action.payload, state.pendingRecs),
			);
		},
		setRecRates(state, action: PayloadAction<PendingRec[] | undefined>) {
			// Incoming recs have no live fields, so preserve any live rates we
			// already know about before replacing state.
			const merged = withLiveRates(action.payload, [
				...state.recRates,
				...state.pendingRecs,
			]);

			// Update the state of all rec rates
			state.recRates = merged;

			// Update the state of pending rec rates
			state.pendingRecs = filterRecs(merged);
		},
		setPolling(state, action: PayloadAction<boolean>) {
			state.polling = action.payload;
		},
		setUploading(state, action: PayloadAction<boolean>) {
			state.uploading = action.payload;
		},
		setAllRowsSelected(state, action: PayloadAction<boolean>) {
			state.allRowsSelected = action.payload;
		},
		togglePolling(state) {
			state.polling = !state.polling;
		},
		toggleUploading(state) {
			state.uploading = !state.uploading;
		},
		updatePending(state, action: PayloadAction<PendingRec>) {
			const baseRecs = removeRec(state.pendingRecs, action.payload.stay_date);

			if (action.payload.rec_status === RecReviewStatus.P) {
				// If the rec is pending, add it to the list
				baseRecs.push(action.payload);
			}
			state.pendingRecs = baseRecs;
		},
	},
});

export const {
	setJobId,
	setPendingRecs,
	setPolling,
	setRecRates,
	setUploading,
	setAllRowsSelected,
	togglePolling,
	toggleUploading,
	updatePending,
} = rateUploadSlice.actions;

export default rateUploadSlice.reducer;
